// ---------------------------------------------------------------------------
// Firestore — Serviço da coleção `user_books`
// Schema conforme lura-context.md §4
// ---------------------------------------------------------------------------

import {
  collection,
  deleteDoc,
  deleteField,
  type DocumentData,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  type Query,
  type QueryDocumentSnapshot,
  query,
  serverTimestamp,
  startAfter,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  Timestamp,
  type QuerySnapshot,
} from "firebase/firestore";
import { createDocSettled, settleWrite } from "../../lib/firebase/writeSettle";
import { resilientOnSnapshot } from "../../lib/firebase/resilientSnapshot";
import { withReadTimeout } from "../../lib/firebase/readTimeout";
import { captureException } from "../../lib/monitoring";
import { toMs } from "../../utils/timestampUtils";
import { db } from "../../lib/firebase/config";
import { noteStatusChange } from "../../lib/defaultStatusTracking";
import {
  calculateReadingProgress,
  isBookFinished,
} from "../../features/books/utils/bookMath";
import { isFutureIsoDate, isoToLocalDate } from "../../features/books/utils/finishedDate";
import { ownershipOf, type BookOwnership } from "../../features/books/utils/bookOwnership";
import {
  applyCoverResolution,
  type CustomCoverSource,
} from "../../features/books/utils/bookCover";
import { parseBookIdToInput, resolveBookIdentityBackground } from "./discoveryService";

/**
 * Ciclo de leitura. ⚠️ `"owned"` NÃO significa mais "tenho" — significa "tenho,
 * sem intenção de leitura declarada". Posse virou eixo próprio (`ownership`);
 * ver `features/books/utils/bookOwnership.ts` para o modelo completo.
 */
export type BookStatus = "want_to_read" | "reading" | "read" | "owned";
export type BookFormat = "physical" | "kindle";

export interface UserBook {
  id: string;
  userId: string;
  bookId: string; // ID da Google Books API
  title: string;
  author: string;
  coverUrl: string;
  status: BookStatus;
  /**
   * Lógica Físico vs Kindle (regra firebase-db):
   * - physical: progress = página atual, totalPages = total de páginas
   * - kindle:   progress = porcentagem (0-100), totalPages ignorado
   */
  format: BookFormat;
  progress: number;
  totalPages: number;
  rating: number | null;
  /** ID canônico resolvido em background por resolveBookIdentity (isbn:..., google:..., apple:...). */
  canonicalBookId?: string;
  /**
   * §8a: formato da EDIÇÃO no catálogo (denormalizado de book_meta.format
   * no add e no heal da BookDetail) — ≠ `format` acima, que é o meio de
   * leitura escolhido pelo usuário. "ebook" liga o selo E-book nos cards.
   */
  editionFormat?: "physical" | "ebook" | null;
  /**
   * Como o exemplar ENTROU na estante, quando isso importa: `"scanner"` =
   * código de barras lido pela câmera (badge secreta Bip!, BACKLOG §0 item 11
   * F4). Gravado só no create; ausente = qualquer outro caminho.
   */
  addedVia?: "scanner";
  /**
   * Posse — eixo INDEPENDENTE do `status`. Ausente/null = não tem o livro.
   * ≠ `format` (meio de leitura) e ≠ `editionFormat` (formato da edição no
   * catálogo): aqui a pergunta é "este exemplar é seu?".
   *
   * ⚠️ Lazy: docs anteriores ao campo não o têm. **Nunca leia `.ownership`
   * direto** — use `ownershipOf`/`isBookOwned` de
   * `features/books/utils/bookOwnership.ts`, que derivam o legado a partir de
   * `status === "owned"`.
   */
  ownership?: BookOwnership | null;
  /**
   * "Já li, mostra tudo" no mural de notas públicas deste livro (F1).
   *
   * Preferência sobre ESTE livro, então mora no exemplar e some junto com ele.
   * Não é client-locked: destravar o próprio spoiler não move economia nenhuma.
   * Ausente = ainda protegido pelo progresso.
   */
  notesUnlockedAt?: unknown;
  /** Top 3 favoritos na estante (máx. 3 com `true` na UI). */
  isFavorite?: boolean;
  /**
   * Posição escolhida no Top 3 do perfil (1, 2 ou 3). Ausente = nunca
   * reordenado; a ordem cai na recência. Ver `utils/favoriteRank.ts`.
   */
  favoriteRank?: number;
  /** Quantas vezes o livro foi concluído. Começa em 1 na primeira conclusão. */
  readCount?: number;
  /**
   * Frase do usuário sobre a leitura, usada como citação do card do Studio.
   *
   * ⚠️ **Não é a resenha** (`book_reviews`) nem nota privada (`notes`). É a
   * legenda do card, e mora aqui porque o card é do livro-do-usuário: a mesma
   * frase tem que reaparecer quando ele abrir o Studio de novo pelo
   * "Compartilhar Conquista", meses depois.
   *
   * Existe porque até 05/08 (BUG-83) esta frase era estado local do
   * `BookCompletionModal` e morria ao fechar — então o card só tinha citação na
   * janela de segundos entre digitar e compartilhar, e livro já concluído nunca
   * mais conseguia uma.
   *
   * ⚠️ `user_books` é `allow read: if isAuthed()`. Não é conteúdo publicado,
   * mas também não é privado como as `notes` — a UI comunica isso pelo próprio
   * enquadramento ("vai no card").
   */
  cardCaption?: string;
  /**
   * Capa escolhida pelo usuário para ESTE exemplar (`docs/systems/user-book-cover.md`).
   *
   * ⚠️ **CLIENT-LOCKED** — quem grava é a callable `setUserBookCover`, e as
   * `firestore.rules` recusam qualquer escrita do cliente nos quatro campos
   * `customCover*`. Isso não é zelo: `user_books` é `allow read: if isAuthed()`
   * e a estante pública renderiza este documento para desconhecidos, então a
   * capa custom é conteúdo publicado e precisa do mesmo gate de moderação que
   * avatar e banner. Moderar o asset no Cloudinary não impede nada enquanto o
   * CAMPO for client-writable (docblock de `account/profileImage.ts`).
   *
   * ⚠️ E é campo SEPARADO de `coverUrl` por medição, não por gosto:
   * `useBookDetailState` sobrescreve `coverUrl` incondicionalmente quando o
   * enrichment acha capa melhor no catálogo — capa custom gravada lá seria
   * apagada em silêncio na próxima abertura do livro.
   */
  customCoverUrl?: string | null;
  /** `public_id` do asset no Cloudinary — é o que permite apagar o anterior. */
  customCoverPublicId?: string | null;
  customCoverSource?: CustomCoverSource | null;
  customCoverAt?: Timestamp | null;
  /**
   * Permissão para a foto enviada virar capa do catálogo, por IMAGEM
   * (`publicId`), e o vínculo com a promoção — fatia C de
   * `docs/systems/catalog-cover.md` §2.2. **CLIENT-LOCKED**: quem grava é
   * `setUserBookCover` (ação `consent`) e `adminPromoteReaderCover`.
   */
  customCoverConsent?: {
    granted: boolean;
    publicId: string;
    via: "in_app" | "support";
    at: Timestamp | null;
    textVersion: string | null;
  } | null;
  /** Existe SÓ enquanto esta foto é a capa do catálogo. */
  customCoverPromotion?: { auditId: string; canonicalId: string; at: Timestamp | null } | null;
  /**
   * Capa do CATÁLOGO, crua — **derivada em memória, NÃO existe no Firestore**.
   *
   * `coverUrl` carrega o valor EFETIVO (custom > catálogo) desde a hidratação,
   * para que as ~25 superfícies de capa do app não precisem saber que a
   * escolha do usuário existe. Quem decide sobre o catálogo — a auto-cura do
   * BUG-171 e o backfill da estante — lê este campo. Ver
   * `features/books/utils/bookCover.ts`.
   */
  catalogCoverUrl?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  finishedAt?: Timestamp | null;
  /**
   * Quando a leitura COMEÇOU — o par de `finishedAt` na outra ponta.
   *
   * Não é `createdAt`: "entrou na estante" e "comecei a ler" só coincidem no
   * livro adicionado direto como "Lendo". Num "Quero ler" que virou "Lendo"
   * meses depois, `createdAt` é o dia do desejo, não o da leitura — e era o
   * único dado que existia, então a página do livro não tinha como responder
   * "quando eu comecei?" (feedback de 15/09). Gravado em todo caminho que
   * ENTRA em "reading" e editável pelo chip do hero (`StartedDateSheet`).
   * Ausente/`null` = não declarado, e o chip pede a data em vez de chutar.
   */
  startedAt?: Timestamp | null;
}

export type NewUserBook = Omit<UserBook, "id" | "createdAt" | "updatedAt" | "finishedAt" | "startedAt">;

// ---------------------------------------------------------------------------
// Leitura (tempo real)
// ---------------------------------------------------------------------------

/**
 * Opções de leitura de TODA lista de `user_books`.
 *
 * `serverTimestamps: "estimate"` não é detalhe: sem ele, `data()` devolve
 * **`null`** em `updatedAt` enquanto a escrita está pendente de ack do servidor
 * — e toda superfície que ordena por `updatedAt` (hero da Home, carrossel do
 * Perfil) via o livro RECÉM-marcado despencar pro fim da lista e voltar pro
 * topo meio segundo depois. A ordem da própria query não sofria disso (o SDK
 * estima localmente pra ordenar); só o valor lido é que vinha nulo, então as
 * duas discordavam. Com a estimativa, o campo nunca é nulo e as duas
 * concordam. O valor estimado é substituído pelo real no ack.
 */
const LIST_READ_OPTS = { serverTimestamps: "estimate" } as const;

/**
 * Único caminho de `DocumentSnapshot` → `UserBook`.
 *
 * Existe para a **capa efetiva** ser resolvida uma vez, aqui, em vez de nas ~25
 * superfícies que leem `coverUrl` (ver `features/books/utils/bookCover.ts`).
 * Todo ponto de leitura de `user_books` passa por ele — subscription, página
 * seguinte, estante pública e leitura avulsa.
 *
 * ⚠️ Hidratação nova entra aqui. Montar `{ id, ...data() }` à mão de novo
 * devolve a capa do catálogo por cima da escolha do usuário, sem erro nenhum:
 * o tipo bate, o build passa e a tela mostra a capa errada.
 */
function hydrateUserBook(docSnap: {
  id: string;
  data: (opts?: typeof LIST_READ_OPTS) => DocumentData | undefined;
}): UserBook {
  return applyCoverResolution({
    id: docSnap.id,
    ...docSnap.data(LIST_READ_OPTS),
  } as UserBook);
}

/**
 * Tamanho da janela da estante — **um número só para todas as superfícies**.
 *
 * ## Por que é uma constante compartilhada, e não um default por função
 *
 * Existiam duas janelas independentes: a do dono (`UserBooksContext`, via
 * `subscribeToUserBooksFirstPage`) e a das superfícies públicas (perfil e
 * estante de outra pessoa, via `fetchUserBooksOnce`). As duas valiam 100 por
 * coincidência, não por acordo.
 *
 * No fix do BUG-76 só a primeira foi levantada, e o resultado foi um estado
 * pior que o bug: a meta anual (contada no servidor) dizia **8**, e a estante
 * logo abaixo listava **3** livros em 2026 — o mesmo número, na mesma tela,
 * discordando de si mesmo. Duas janelas que precisam concordar não podem ser
 * dois literais em arquivos diferentes.
 *
 * ## Por que 400
 *
 * ⚠️ **`limit(N)` custa `min(N, docs existentes)`, não N.** Para a estante
 * mediana (28 livros, medido sobre a base inteira) 100 e 400 leem exatamente a
 * mesma coisa — este número não é um orçamento de leitura, é um teto de
 * segurança. 400 cobre a maior estante real (184) com folga de 2×.
 *
 * Não é a resposta definitiva: acima disso as contagens derivadas desta janela
 * voltam a subcontar. A resposta é `count()` para os contadores (já feito para
 * a meta anual — ver `shelfCounts.ts`) e paginação por aba para as listas, que
 * os índices já suportam.
 */
export const SHELF_PAGE_SIZE = 400;

export interface UserBooksPage {
  books: UserBook[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
}

export function subscribeToUserBooksFirstPage(
  userId: string,
  pageSize: number,
  callback: (page: UserBooksPage) => void,
  onError?: (error: Error) => void,
): () => void {
  // orderBy("updatedAt", "desc") garante ordem determinística — livros recentes
  // sempre na primeira página. Sem isso, Firestore retornava ordem indefinida
  // e o dedup client-side podia não achar livros antigos. Index composto
  // (userId, updatedAt) já existe em firestore.indexes.json.
  const q = query(
    collection(db, "user_books"),
    where("userId", "==", userId),
    orderBy("updatedAt", "desc"),
    limit(pageSize),
  );

  return resilientOnSnapshot(
    (next, error) => onSnapshot(q, next, error),
    (snapshot: QuerySnapshot) => {
      const books = snapshot.docs.map(hydrateUserBook);
      callback({
        books,
        lastDoc: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null,
      });
    },
    (error) => onError?.(error),
  );
}

/**
 * Estante de OUTRO usuário, leitura única (perfil público).
 *
 * `getDocs` e não `onSnapshot` de propósito: o perfil público é uma visita de
 * passagem — abrir um listener em tempo real na estante de terceiro custa uma
 * conexão viva por visita sem nenhum ganho (o dono não está editando enquanto
 * o visitante olha).
 *
 * ⚠️ CUSTO: é uma leitura NOVA numa superfície pública, ~`pageSize` docs por
 * visita de perfil. O limite existe pra isso: as seções do perfil público
 * (lendo agora, últimos lidos, top 3, contagens da estante) são todas
 * derivadas desta única página, em vez de uma query por seção.
 *
 * `user_books` é `allow read: if isAuthed()` — visitante deslogado não chega
 * aqui (a rota `/profile/:username` é Guarded).
 */
export async function fetchUserBooksOnce(
  userId: string,
  pageSize = SHELF_PAGE_SIZE,
): Promise<UserBook[]> {
  const q = query(
    collection(db, "user_books"),
    where("userId", "==", userId),
    orderBy("updatedAt", "desc"),
    limit(pageSize),
  );
  const snapshot = await withReadTimeout(getDocs(q));
  return snapshot.docs.map(hydrateUserBook);
}

/**
 * Janela da AMOSTRA de estante (perfil público e vitrine `/lendo`).
 *
 * ## Por que existe, ao lado de `SHELF_PAGE_SIZE` (BACKLOG §0 item 8, 24/09)
 *
 * O perfil público lia a estante inteira (até 400 docs) para desenhar 16
 * itens: 3 "lendo agora", 10 "últimos lidos", o Top 3 e 2 mini-capas por tile.
 * Os NÚMEROS da tela já vêm do servidor (`useShelfCounts`,
 * `useBooksFinishedInYear`) — a página só alimenta listas curtas e o piso
 * enquanto a contagem não chega. Medido em 24/09 sobre a base inteira (231
 * estantes): p50 5 · p90 49 · p99 291 · máx 490 docs. Para 90% das pessoas
 * 50 docs JÁ É a estante inteira, e a leitura fica idêntica à de antes.
 *
 * Acima disso a janela vem incompleta, e o que falta é pedido por consultas
 * pequenas (`fetchShelfSample`). Simulado sobre a base de 24/09: média 23,8 →
 * 13,5 docs por visita de perfil, máx 400 → 68, nenhuma estante mais cara; a
 * `/lendo` cai para média 8,7, máx 21. ⚠️ Os 3 favoritos da maior estante
 * estão FORA dos 50 mais recentes — é por isso que o complemento dos
 * favoritos é incondicional quando a janela enche.
 *
 * ⚠️ A estante pública (`/profile/:u/shelf/:status`) NÃO usa isto: ela lista a
 * prateleira inteira e precisa concordar com a contagem do servidor — lá vale
 * `SHELF_PAGE_SIZE` (BUG-76, `shelfWindowParity.test.ts`).
 */
export const SHELF_SAMPLE_WINDOW = 50;

/** O que a superfície desenha — cada recorte com o mínimo de itens que precisa. */
export interface ShelfSampleNeeds {
  reading?: number;
  read?: number;
  favorites?: boolean;
  want_to_read?: number;
  owned?: number;
}

/** O perfil público: 3 lendo agora, 10 últimos lidos, Top 3, 2 capas por tile. */
export const PROFILE_SHELF_NEEDS: ShelfSampleNeeds = {
  reading: 3,
  read: 10,
  favorites: true,
  want_to_read: 2,
  owned: 2,
};

/**
 * UM livro "atual" (`selectCurrentReadingBook`: "lendo" mais recente, senão
 * "lido" mais recente) — vitrine `/lendo` e o tema da leitura atual. Janela
 * menor que a do perfil: a página recebe tráfego frio de link-na-bio e só
 * precisa de 1 doc.
 */
export const CURRENT_BOOK_NEEDS: ShelfSampleNeeds = { reading: 1, read: 1 };
export const CURRENT_BOOK_WINDOW = 20;

/**
 * Teto da consulta de "lendo agora" no complemento. Ela não tem `orderBy` (o
 * índice `userId+status+updatedAt` não existe, e criar um índice por isso não
 * vale) — então traz TODOS os "lendo" e ordena em memória. Máximo medido em
 * 24/09: 12 livros em leitura numa estante; acima de 20 a escolha deixa de
 * ser exata (mostra 3 dos 20 em vez dos 3 mais recentes).
 */
const SAMPLE_READING_CAP = 20;
/** Os favoritos são no máximo 3 (`MAX_FAVORITES`); a folga cobre legado. */
const SAMPLE_FAVORITES_CAP = 10;

export interface ShelfSample {
  /** Ordenados por `updatedAt` desc, como `fetchUserBooksOnce` — os consumidores dependem disso. */
  books: UserBook[];
  /** `true` = a janela já era a estante inteira (nenhum complemento foi pedido). */
  complete: boolean;
}

/**
 * Amostra da estante de OUTRA pessoa (ou do dono, fora do `UserBooksContext`)
 * — só o que uma superfície de destaques desenha, não a estante inteira.
 *
 * 1. Janela de `window` docs por `updatedAt` desc. Veio menor que a janela =
 *    é a estante inteira: devolve já, sem nenhuma consulta a mais.
 * 2. Janela cheia: para cada recorte pedido em `needs` que a janela NÃO
 *    cobre, uma consulta pequena, em paralelo (os favoritos sempre — a janela
 *    não diz nada sobre eles). Tudo com índices que já existem.
 *
 * Por que a janela basta quando cobre: ela é o topo por `updatedAt`, então o
 * 1º "lendo" dentro dela É o "lendo" mais recente da estante inteira, e o
 * mesmo vale para "lido" e para as capas.
 *
 * ⚠️ Aproximação conhecida: "últimos lidos" ordena por `finishedAt ||
 * updatedAt`. Um livro terminado há pouco fica fora da janela só se ≥ 50
 * outros livros foram editados depois — e aí o complemento o busca por
 * `finishedAt` (livro "lido" SEM `finishedAt` — 45% dos lidos em 24/09, quase
 * todos de import — fica de fora só nesse caso).
 *
 * Um complemento que falha não derruba a amostra: a janela é desenhada e a
 * falha é reportada (a alternativa — perfil sem estante — mente mais).
 */
export async function fetchShelfSample(
  userId: string,
  needs: ShelfSampleNeeds,
  window = SHELF_SAMPLE_WINDOW,
): Promise<ShelfSample> {
  const base = collection(db, "user_books");
  const mine = where("userId", "==", userId);
  const first = await withReadTimeout(
    getDocs(query(base, mine, orderBy("updatedAt", "desc"), limit(window))),
  );
  const books = first.docs.map(hydrateUserBook);
  if (books.length < window) return { books, complete: true };

  const inWindow = (pred: (b: UserBook) => boolean) => books.filter(pred).length;
  const extra: Query<DocumentData>[] = [];
  if (needs.reading && inWindow((b) => b.status === "reading") < needs.reading) {
    extra.push(query(base, mine, where("status", "==", "reading"), limit(SAMPLE_READING_CAP)));
  }
  if (needs.read && inWindow((b) => b.status === "read") < needs.read) {
    extra.push(
      query(base, mine, where("status", "==", "read"), orderBy("finishedAt", "desc"), limit(needs.read)),
    );
  }
  if (needs.favorites) {
    extra.push(query(base, mine, where("isFavorite", "==", true), limit(SAMPLE_FAVORITES_CAP)));
  }
  if (needs.want_to_read && inWindow((b) => b.status === "want_to_read") < needs.want_to_read) {
    extra.push(query(base, mine, where("status", "==", "want_to_read"), limit(needs.want_to_read)));
  }
  if (needs.owned && inWindow((b) => ownershipOf(b) !== null) < needs.owned) {
    // Posse mora em dois lugares: o campo `ownership` e o legado
    // `status: "owned"` sem o campo (115 docs em 24/09).
    extra.push(
      query(base, mine, where("ownership", "in", ["physical", "digital"]), limit(needs.owned)),
      query(base, mine, where("status", "==", "owned"), limit(needs.owned)),
    );
  }

  const settled = await Promise.allSettled(extra.map((q) => withReadTimeout(getDocs(q))));
  const byId = new Map(books.map((b) => [b.id, b]));
  for (const result of settled) {
    if (result.status === "rejected") {
      captureException(result.reason, { kind: "shelf-sample-complement" });
      continue;
    }
    for (const d of result.value.docs) {
      if (!byId.has(d.id)) byId.set(d.id, hydrateUserBook(d));
    }
  }
  const merged = [...byId.values()].sort((a, b) => toMs(b.updatedAt) - toMs(a.updatedAt));
  return { books: merged, complete: false };
}

export async function loadMoreUserBooks(
  userId: string,
  pageSize: number,
  lastDoc: QueryDocumentSnapshot<DocumentData> | null,
): Promise<UserBooksPage> {
  if (!lastDoc) return { books: [], lastDoc: null };

  const q = query(
    collection(db, "user_books"),
    where("userId", "==", userId),
    orderBy("updatedAt", "desc"),
    startAfter(lastDoc),
    limit(pageSize),
  );

  const snapshot = await getDocs(q);
  const books = snapshot.docs.map(hydrateUserBook);
  return {
    books,
    lastDoc: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null,
  };
}

// ---------------------------------------------------------------------------
// Dedup helpers — resolução de canonical + lookup server-side
// ---------------------------------------------------------------------------

const CANONICAL_PREFIX_RE = /^(isbn|apple|google):/;
const LEGACY_HYPHEN_RE = /^(apple|google)-(.+)$/;

/**
 * Resolve o canonical (`isbn:`/`apple:`/`google:`) a partir de um bookId
 * arbitrário consultando `book_meta` e `book_meta_aliases`. Devolve `null`
 * quando nenhum dos lookups bate (livro novo ainda não enriquecido).
 *
 * Custa 1-2 reads em Firestore — barato e crucial pra dedup server-side.
 * Usado por `addBook` antes de gravar pra detectar se a obra já tá na
 * estante mesmo quando o `bookId` legacy difere do canonical.
 */
export async function resolveCanonicalFromBookId(bookId: string): Promise<string | null> {
  if (!bookId) return null;

  // 1. Canonical direto (`isbn:X`, `apple:X`, `google:X`) — só confirma que
  //    o doc existe em book_meta antes de afirmar canonical.
  if (CANONICAL_PREFIX_RE.test(bookId)) {
    const metaSnap = await getDoc(doc(db, "book_meta", bookId));
    if (metaSnap.exists()) return bookId;
    // Pode existir só como alias (raro mas defensivo)
    return bookId;
  }

  // 2. Legacy hyphen `apple-X`/`google-X` (formato pré-refactor Work+Edition).
  const hyphen = bookId.match(LEGACY_HYPHEN_RE);
  if (hyphen) {
    const rawId = hyphen[2];
    // Alias usa só o ID raw (sem prefixo nem hífen)
    const aliasSnap = await getDoc(doc(db, "book_meta_aliases", rawId));
    if (aliasSnap.exists()) {
      const data = aliasSnap.data() as { canonicalId?: string } | undefined;
      if (data?.canonicalId) return data.canonicalId;
    }
    // Fallback: chuta canonical direto `apple:rawId` ou `google:rawId`
    const guessed = `${hyphen[1]}:${rawId}`;
    const metaSnap = await getDoc(doc(db, "book_meta", guessed));
    if (metaSnap.exists()) return guessed;
    return null;
  }

  // 3. Outros formatos (Google volume id puro, brasilapi-, etc) — só alias.
  const aliasSnap = await getDoc(doc(db, "book_meta_aliases", bookId));
  if (aliasSnap.exists()) {
    const data = aliasSnap.data() as { canonicalId?: string } | undefined;
    if (data?.canonicalId) return data.canonicalId;
  }

  return null;
}

/**
 * Busca no `user_books` do usuário um doc cujo `canonicalBookId` bate. Usado
 * pelo `addBook` pra detectar duplicata mesmo quando o livro antigo tá
 * fora da primeira página (paginação no `UserBooksContext`).
 *
 * Precisa do index composto `(userId ASC, canonicalBookId ASC)` declarado
 * em `firestore.indexes.json`.
 */
export async function findExistingUserBookByCanonical(
  userId: string,
  canonicalBookId: string,
): Promise<UserBook | null> {
  if (!userId || !canonicalBookId) return null;
  const q = query(
    collection(db, "user_books"),
    where("userId", "==", userId),
    where("canonicalBookId", "==", canonicalBookId),
    limit(1),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const docSnap = snap.docs[0];
  return hydrateUserBook(docSnap);
}

/**
 * As grafias sob as quais um MESMO id de catálogo pode ter sido gravado em
 * `user_books.bookId`: o próprio, e o par hyphen↔colon do legado
 * (`apple-657046481` ↔ `apple:657046481`). Espelho do que
 * `findUserBookByAnyId` faz em memória — aqui vira uma consulta por grafia.
 * Sem duplicatas e na ordem em que devem ser tentadas (o id cru primeiro).
 */
export function userBookIdVariants(lookupId: string): string[] {
  const id = lookupId.trim();
  if (!id) return [];
  const colon = id.replace(/^(apple|google)-/, "$1:");
  const hyphen = id.replace(/^(apple|google):/, "$1-");
  return [...new Set([id, colon, hyphen])];
}

/**
 * Busca UM exemplar do usuário por qualquer id que o identifique — a versão
 * "vai ao Firestore" do `findUserBookByAnyId` (BUG-215).
 *
 * Existe para a recuperação da sessão de Foco: a estante em memória é uma
 * JANELA (`SHELF_PAGE_SIZE` por `updatedAt`), e o livro da sessão pode estar
 * fora dela. Ordem: `bookId` em cada grafia, depois `canonicalBookId` em cada
 * grafia. Cada consulta é 1 leitura no pior caso; o chamador só chega aqui
 * quando a estante em memória já não resolveu.
 */
export async function fetchUserBookByAnyId(
  userId: string,
  lookupId: string,
): Promise<UserBook | null> {
  if (!userId) return null;
  const variants = userBookIdVariants(lookupId);
  for (const id of variants) {
    const q = query(
      collection(db, "user_books"),
      where("userId", "==", userId),
      where("bookId", "==", id),
      limit(1),
    );
    const snap = await getDocs(q);
    if (!snap.empty) return hydrateUserBook(snap.docs[0]);
  }
  for (const id of variants) {
    const byCanonical = await findExistingUserBookByCanonical(userId, id);
    if (byCanonical) return byCanonical;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Escrita
// ---------------------------------------------------------------------------

export interface AddBookResult {
  /** ID do user_book — pode ser de doc novo (`alreadyExists: false`) ou existente (`true`). */
  id: string;
  /** True quando o livro já estava na estante (dedup server-side bateu via canonicalBookId). */
  alreadyExists: boolean;
  /** Doc preexistente quando alreadyExists=true — caller pode mostrar modal ou navegar pra ele. */
  existing?: UserBook;
}

/**
 * Adiciona um novo livro à estante do usuário, com dedup server-authoritative.
 *
 * Antes de criar, tenta resolver o `canonicalBookId` (1-2 reads via
 * book_meta_aliases/book_meta) e consulta o Firestore por outro user_book do
 * mesmo `canonicalBookId`. Se existir, devolve o ID do doc preexistente
 * com `alreadyExists: true` em vez de criar duplicata — evitando o caso real
 * em que o shelf paginado (PAGE_SIZE=50) deixa livros antigos fora da
 * memória client e o dedup só-client falhar.
 *
 * Quando o status inicial for "read", o trigger server-side `onUserBookWritten`
 * cuida do incremento de stats (totalBooksRead, lifetimeBooksRead, streak).
 *
 * @returns `AddBookResult` com `id`, `alreadyExists` e opcional `existing`.
 */
export async function addBook(
  book: NewUserBook,
  opts?: { startDate?: string; finishedDate?: string | null },
): Promise<AddBookResult> {
  // 1. Tentar resolver canonical sync se ainda não veio
  let canonical = book.canonicalBookId ?? null;
  if (!canonical) {
    try {
      canonical = await resolveCanonicalFromBookId(book.bookId);
    } catch (err) {
      // Lookup falhou — segue sem canonical (best effort)
      console.warn("[Lura] resolveCanonicalFromBookId falhou:", err);
    }
  }

  // 2. Dedup server-side: se canonical conhecido, query Firestore por outro
  //    user_book do mesmo livro. Evita criar duplicata mesmo quando o doc
  //    antigo tá fora da primeira página paginada do UserBooksContext.
  if (canonical) {
    try {
      const existing = await findExistingUserBookByCanonical(book.userId, canonical);
      if (existing) {
        // Backfill canonicalBookId no existente quando estiver faltando.
        // Custa 1 update mas amortiza dedup pro futuro (próximo addBook
        // pega via index direto).
        if (!existing.canonicalBookId) {
          try {
            await settleWrite(updateDoc(doc(db, "user_books", existing.id), {
              canonicalBookId: canonical,
              updatedAt: serverTimestamp(),
            }), "booksService.addBook");
          } catch {
            // Backfill best-effort — ignora falha
          }
        }
        return { id: existing.id, alreadyExists: true, existing };
      }
    } catch (err) {
      console.warn("[Lura] findExistingUserBookByCanonical falhou:", err);
    }
  }

  // 3. Não tem duplicata — cria normalmente, populando canonicalBookId
  //    sincronamente quando resolvido (evita gap onde o doc existe sem canon
  //    até o background resolveBookIdentity rodar).
  // ⚠️ `new Date(iso)` lia a data só-data como meia-noite UTC, que em Brasília
  // é o DIA ANTERIOR — um livro concluído em 1º de janeiro contava no ano
  // passado, em toda superfície que agrupa por ano. Ver `finishedDate.ts`.
  const tsFrom = (iso?: string) => {
    const local = iso ? isoToLocalDate(iso) : null;
    return local ? Timestamp.fromDate(local) : null;
  };

  // Sem data declarada, `finishedAt` é `null` — omitir e passar `null`
  // explícito dão no MESMO resultado.
  //
  // ⚠️ Nem sempre foi assim, e as duas metades desta regra vieram de lados
  // diferentes. A catalogação retroativa (livro lido em 2019 entrando na
  // estante hoje) introduziu o `null` explícito porque `tsFrom(undefined)`
  // carimbava a data de hoje; a onda de 01/08 foi além e tirou o carimbo do
  // próprio `tsFrom`, porque o mesmo estrago acontecia por outros caminhos —
  // marcar "Lido" pelo switcher enchia o ano corrente de livros antigos. Hoje
  // NENHUM caminho inventa data: ou o usuário declarou, ou o campo é `null`.
  //
  // `finishedAt: null` é estado suportado de primeira classe: cai no grupo
  // "Sem data", que é onde vive o gatilho de declarar a data, e fica fora da
  // contagem do ano (mesmo estado que o onboarding grava).
  const finishedAt =
    book.status !== "read" ? null : opts?.finishedDate === null ? null : tsFrom(opts?.finishedDate);

  const docData: Record<string, unknown> = {
    ...book,
    // Normaliza a posse na criação: `ownershipOf` devolve o que o caller
    // escolheu quando é válido e, quando não, sustenta o invariante
    // (`status: "owned"` ⟹ posse não-nula). Também troca `undefined` por
    // `null` explícito — o SDK recusa `undefined` num write.
    ownership: ownershipOf(book),
    // ⚠️ O `?? serverTimestamp()` é OBRIGATÓRIO desde que `tsFrom` parou de
    // carimbar a hora atual: `createdAt` é "quando o livro entrou na estante",
    // o app sabe a resposta e ela nunca é nula. A assimetria com `finishedAt`
    // logo abaixo é o ponto — só o usuário sabe quando terminou de ler.
    createdAt: tsFrom(opts?.startDate) ?? serverTimestamp(),
    updatedAt: serverTimestamp(),
    finishedAt,
    // Só o livro que nasce "Lendo" ganha início: é o único status em que a
    // folha pergunta "Início" como a data em que a leitura começou. No "Lido"
    // o mesmo campo vem pré-preenchido com hoje ao lado de uma conclusão que
    // pode ser de 2019 — gravá-lo seria a data inventada que `finishedAt`
    // aprendeu a não escrever.
    startedAt: book.status === "reading" ? tsFrom(opts?.startDate) : null,
  };
  if (canonical && !docData.canonicalBookId) {
    docData.canonicalBookId = canonical;
  }

  const docRef = await createDocSettled(collection(db, "user_books"), docData, "booksService.addBook");
  // Popula book_meta em background (non-blocking) para o Discovery Engine.
  // Mesmo quando canonical já resolveu, o enrichment full (sources, sinopse,
  // covers) ainda pode estar pendente — esse call cobre isso.
  resolveBookIdentityBackground(parseBookIdToInput(book.bookId, book.title, book.author));
  return { id: docRef.id, alreadyExists: false };
}

/**
 * Atualiza campos arbitrários de um livro existente.
 * Usa Partial para que o chamador só passe os campos necessários.
 */
export async function updateBook(
  bookId: string,
  updates: Partial<Omit<UserBook, "id" | "userId" | "createdAt">>
): Promise<void> {
  const bookRef = doc(db, "user_books", bookId);
  await settleWrite(updateDoc(bookRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  }), "booksService.updateBook");
}

/**
 * Atualiza somente o progresso de leitura de um livro.
 *
 * Quando o livro chega a 100% (kindle) ou totalPages (físico) E userId é
 * fornecido, delega para updateBookStatus — garantindo que o fluxo atômico
 * de incremento de stats e conquistas seja sempre executado.
 *
 * @param userId - UID do usuário; obrigatório para disparar a transação de stats.
 */
export async function updateProgress(
  bookId: string,
  progress: number,
  format: BookFormat,
  totalPages?: number,
  userId?: string,
  opts?: { finishedDate?: string },
): Promise<void> {
  const bookRef = doc(db, "user_books", bookId);
  const snap = await getDoc(bookRef);
  const prev = snap.exists() ? (snap.data() as Partial<UserBook>) : {};
  const oldTotal = typeof prev.totalPages === "number" ? prev.totalPages : 0;
  const resolvedTotal =
    totalPages !== undefined ? totalPages : oldTotal;

  const normalizedProgress = calculateReadingProgress(
    progress,
    format,
    resolvedTotal || undefined,
  );
  const finished = isBookFinished(
    normalizedProgress,
    format,
    resolvedTotal || undefined,
  );

  // Contabilidade de páginas é 100% server-side (BUG-18): a CF
  // updateBookProgress credita trilha diária + totalPagesRead a cada
  // registro, e o trigger onUserBookWritten credita o RESTANTE no
  // fechamento. O mapa legado users.dailyPages (incrementDailyPagesRead)
  // foi aposentado — só recebia o caminho manual e alimentava um gráfico
  // que nunca batia com o total.
  // ⚠️ `prev.status !== "read"`: `updateBookStatus("read")` incrementa
  // `readCount` toda vez que roda. Enquanto a CF proibia marcar um alvo ≤ ao
  // atual, re-confirmar o fim de um livro já lido era difícil de alcançar; com
  // a edição livre de progresso (marca d'água, 31/07) ficou a um toque —
  // "Terminei" num livro já terminado registraria uma releitura fantasma, e o
  // trigger `onUserBookWritten` contaria +1 em `totalReads`. Releitura de
  // verdade continua entrando pelo caminho dela (RereadConfirmModal →
  // updateBookStatus), que é explícito.
  if (finished && userId && prev.status !== "read") {
    await settleWrite(setDoc(
      bookRef,
      {
        progress: normalizedProgress,
        format,
        ...(totalPages !== undefined && { totalPages }),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    ), "booksService.updateProgress");
    await updateBookStatus(bookId, "read", userId, opts);
    return;
  }

  // Auto-avanço de status: registrar progresso num livro "Quero ler"/"Só tenho"
  // (não iniciado) o move pra "Lendo" — o leitor claramente começou. Só quando
  // há progresso real (>0) e o status ainda não é "reading"/"read".
  const shouldStartReading =
    normalizedProgress > 0 &&
    (prev.status === "want_to_read" || prev.status === "owned");

  // ⚠️ Este caminho escreve `status` SEM passar pelo `updateBookStatus`, então
  // carrega a materialização de posse por conta própria. Num doc legado a posse
  // é derivada de `status === "owned"`; sair desse status sem gravá-la faria
  // "registrei uma página" apagar "eu tenho este livro" — em silêncio, e neste
  // caminho específico sem nem um toque no controle de posse pra explicar.
  const ownershipPatch = shouldStartReading
    ? ownershipPatchForStatus(prev, "reading")
    : null;

  await settleWrite(setDoc(
    bookRef,
    {
      progress: normalizedProgress,
      format,
      ...(totalPages !== undefined && { totalPages }),
      ...(shouldStartReading && { status: "reading" as BookStatus, startedAt: serverTimestamp() }),
      ...ownershipPatch,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  ), "booksService.updateProgress");
}

/**
 * Remove permanentemente um livro da estante.
 */
export async function deleteBook(bookId: string): Promise<void> {
  await settleWrite(deleteDoc(doc(db, "user_books", bookId)), "booksService.deleteBook");
}

/**
 * Patch de posse a aplicar junto de uma troca de `status`. `null` = nada a
 * escrever (o campo fica intocado pelo `merge`).
 *
 * Duas responsabilidades, as duas obrigatórias:
 *
 * 1. **Sustenta o invariante** `status === "owned"` ⟹ posse não-nula. Entrar em
 *    "Só tenho" sem posse declarada grava `physical` — o default honesto (era
 *    o significado literal do status antigo, cuja estante se chamava "estante
 *    física"). A UI normalmente já manda o formato escolhido; isto cobre todo
 *    caller que não mandar.
 *
 * 2. **Materializa o legado antes de perdê-lo.** Num doc antigo a posse é
 *    DERIVADA de `status === "owned"`. Sair desse status sem gravar o valor
 *    derivado apagaria a informação em silêncio: quem tinha o livro e começa a
 *    ler passaria a não ter mais. É o caminho mais provável do app inteiro
 *    (inclusive o auto-avanço de `updateProgress`), então é aqui que ele
 *    fica — no choke point, não em cada caller.
 */
function ownershipPatchForStatus(
  prev: Partial<UserBook> | null | undefined,
  nextStatus: BookStatus,
  /** Posse escolhida na MESMA interação (o sheet manda o formato junto). */
  explicit?: BookOwnership,
): { ownership: BookOwnership } | null {
  // ⚠️ `explicit` fica FORA de `ownershipOf`: mesclá-lo no doc antes de derivar
  // faria o alvo e o valor "que já está lá" serem sempre iguais, e a guarda de
  // baixo descartaria justamente a escolha que o usuário acabou de fazer.
  const current = ownershipOf(prev);
  const target: BookOwnership | null =
    explicit ?? (nextStatus === "owned" ? (current ?? "physical") : current);

  // `target === null` só acontece quando o doc não tem posse nem explícita nem
  // derivável — não há o que gravar. E se `prev.ownership` já é o alvo, o
  // campo está correto: escrever de novo só sujaria o histórico da posse.
  if (target === null || prev?.ownership === target) return null;
  return { ownership: target };
}

/**
 * Atualiza o status de leitura de um livro (ex: "reading" → "read").
 *
 * Para "read": grava status + readCount + `finishedAt` SE o chamador declarar
 * a data (ver o bloco dentro do ramo — sem data o campo não é escrito). Stats
 * (streak, totalBooksRead, totalPagesRead) são contabilidade SERVER-SIDE —
 * trigger onUserBookWritten (BUG-18: o fechamento credita só o restante de
 * páginas não-registrado; o que foi logado já entrou via CF).
 *
 * ⚠️ A posse (`ownership`) viaja junto quando precisa — ver
 * `ownershipPatchForStatus`. Por isso o doc é lido em TODOS os caminhos agora,
 * e não só em "read"/"reading": um `+1 read` por troca de status (ação rara e
 * sempre iniciada pelo usuário) em troca de o eixo de posse não vazar.
 */
/**
 * `readCount` de um exemplar que acaba de virar "read" (BUG-328, fundador
 * 06/10): 1ª conclusão vira 1; releitura — que no app sempre passa por
 * "reading" (`confirmReread`) — soma 1; desvio de status num livro já lido
 * (Lido → Quero ler/Só tenho → Lido) MANTÉM, porque não houve leitura. Espelho
 * de `nextReadCountOnRead` em `functions/src/focus/registerPages.ts`, e a mesma
 * régua decide no servidor se o fechamento credita (`resolveCompletionDeltas`).
 */
export function nextReadCountOnRead(prevStatus: unknown, prevReadCount: unknown): number {
  const count =
    typeof prevReadCount === "number" && Number.isFinite(prevReadCount)
      ? Math.max(0, Math.floor(prevReadCount))
      : 0;
  if (count === 0) return 1;
  return prevStatus === "reading" ? count + 1 : count;
}

export async function updateBookStatus(
  bookId: string,
  status: BookStatus,
  _userId?: string,
  opts?: { finishedDate?: string; ownership?: BookOwnership },
): Promise<void> {
  // Idem `addBook`: ancorado no meio-dia local, não na meia-noite UTC.
  const finishedLocal = opts?.finishedDate ? isoToLocalDate(opts.finishedDate) : null;
  const finishedTs = finishedLocal ? Timestamp.fromDate(finishedLocal) : null;

  // Único choke point de troca de status no app — é aqui que dá pra medir se
  // o default "Tenho" (livro adicionado só a uma lista) acerta a intenção.
  noteStatusChange(bookId, status);

  const bookRef = doc(db, "user_books", bookId);
  const snap = await getDoc(bookRef);
  const prev = snap.exists() ? (snap.data() as Partial<UserBook>) : null;

  const ownershipPatch = ownershipPatchForStatus(prev, status, opts?.ownership);

  if (status === "read") {
    // Apenas marca o livro como lido. Stats (streak, totalBooksRead, totalPagesRead)
    // são atualizadas server-side pelo trigger onUserBookWritten em Cloud Functions.

    await settleWrite(setDoc(bookRef, {
      status: "read" as BookStatus,
      // ⚠️ Sem data declarada o campo é OMITIDO — não vira `serverTimestamp()`.
      //
      // Até 01/08 caía em "agora", e o efeito era o oposto do pretendido: quem
      // catalogava estante antiga (marcar "Lido" pelo switcher é UM toque)
      // enchia o ano corrente de livros lidos anos atrás, e a meta anual
      // media isso. Data que o usuário não declarou é dado inventado — e
      // inventado com aparência de verdade, que é o pior tipo: na estante ele
      // lê "2026" e não tem como saber que ninguém escreveu aquilo.
      //
      // Omitir, e não gravar `null`: num `merge` o `null` APAGARIA a data real
      // de quem relê um livro e conclui a releitura sem declarar dia nenhum.
      // Campo ausente e `null` são a mesma coisa para quem lê (`toMs` devolve 0
      // nos dois casos, e `shelfBookDateMs` manda os dois pro grupo "Sem data").
      ...(finishedTs && { finishedAt: finishedTs }),
      readCount: nextReadCountOnRead(prev?.status, prev?.readCount),
      ...ownershipPatch,
      updatedAt: serverTimestamp(),
    }, { merge: true }), "booksService.updateBookStatus");
    return;
  }

  // "reading" após livro já lido = releitura → reseta progresso a 0.
  if (status === "reading") {
    const wasRead = prev?.finishedAt != null;
    await settleWrite(setDoc(bookRef, {
      status,
      ...ownershipPatch,
      updatedAt: serverTimestamp(),
      ...(wasRead && { progress: 0 }),
      // ENTRAR em "Lendo" é o começo da leitura — e aqui "agora" não é data
      // inventada: é o mesmo default que a folha de adicionar já pré-preenche
      // em "Início" (a régua do BUG-240 depende dele). Quem começou antes
      // corrige pelo chip do hero. Só na transição: re-salvar "Lendo" num
      // livro que já está lendo não pode reiniciar a data.
      ...(prev?.status !== "reading" && { startedAt: serverTimestamp() }),
    }, { merge: true }), "booksService.updateBookStatus");
    return;
  }

  await settleWrite(setDoc(bookRef, {
    status,
    ...ownershipPatch,
    updatedAt: serverTimestamp(),
  }, { merge: true }), "booksService.updateBookStatus");
  void finishedTs;
}

/**
 * Declara (ou retira) a posse de um livro — o eixo independente do `status`.
 *
 * `null` = "não tenho". Retirar a posse de um livro em `status: "owned"` o
 * deixaria sem nada a dizer (nem intenção de leitura, nem exemplar), o que
 * viola o invariante do modelo; nesse caso o livro volta para `want_to_read`,
 * que é a leitura honesta de "não tenho mais, mas ainda me interessa". Quem
 * quer que ele suma tem `deleteBook`, que é explícito e avisa o que apaga.
 */
export async function updateBookOwnership(
  bookId: string,
  ownership: BookOwnership | null,
): Promise<void> {
  const bookRef = doc(db, "user_books", bookId);

  if (ownership === null) {
    const snap = await getDoc(bookRef);
    const prev = snap.exists() ? (snap.data() as Partial<UserBook>) : null;
    if (prev?.status === "owned") {
      await settleWrite(setDoc(bookRef, {
        status: "want_to_read" as BookStatus,
        ownership: null,
        updatedAt: serverTimestamp(),
      }, { merge: true }), "booksService.updateBookOwnership");
      return;
    }
  }

  await settleWrite(setDoc(bookRef, {
    ownership,
    updatedAt: serverTimestamp(),
  }, { merge: true }), "booksService.updateBookOwnership");
}

/**
 * Define ou corrige a data de conclusão de um livro JÁ marcado como lido.
 *
 * ## Por que isto precisou existir
 *
 * `finishedAt` só era gravado no INSTANTE em que o livro virava "lido" — pelo
 * `AddToShelfSheet`, pelo `ProgressSheet` ou pelo `updateBookStatus`. Quem
 * marcasse sem declarar data (o caminho do onboarding grava `finishedAt: null`
 * de propósito) ou errasse o dia não tinha, em lugar nenhum do app, como
 * arrumar depois. O único jeito era desmarcar e remarcar o livro — que
 * incrementa `readCount` e registra uma releitura que não houve.
 *
 * ## O que esta função NÃO faz — e isto é a parte importante
 *
 * ⚠️ **Não toca em economia.** Nem moeda, nem XP, nem streak, nem
 * `totalBooksRead`. Corrigir uma data é um reparo de dado, não um evento de
 * leitura: pagar por ele seria uma torneira infinita (basta reeditar), e
 * cobrar de volta puniria quem corrige um erro nosso.
 *
 * A garantia é **estrutural**, não uma promessa: o `onUserBookWritten` só entra
 * na transação de stats quando `statusAfter === "read" && statusBefore !==
 * "read"` (`functions/src/books/triggers.ts`). Aqui o status não é escrito e o
 * livro já é "read" antes e depois — o trigger cai no early-return. O faucet de
 * ativação é `isCreate`, e `globalCompletionCount` exige a mesma transição.
 * Por isso a função grava **exclusivamente** `finishedAt` + `updatedAt`: uma
 * escrita de `status` aqui, mesmo com o mesmo valor, seria inofensiva hoje e
 * uma armadilha na primeira vez que alguém relaxasse aquela condição.
 *
 * O que MUDA é a contagem por ano: a meta anual e o agrupamento da estante
 * derivam de `finishedAt` a cada render, direto do listener do
 * `UserBooksContext`. Mover a data de dezembro para janeiro tira um livro de um
 * ano e põe no outro, nas duas pontas, sem recontagem nenhuma.
 *
 * @param isoDate `YYYY-MM-DD` no calendário local, ou `null` para limpar.
 * @throws se a data for futura ou malformada.
 */
export async function updateBookFinishedDate(
  bookId: string,
  isoDate: string | null,
): Promise<void> {
  let finishedAt: Timestamp | null = null;

  if (isoDate) {
    const local = isoToLocalDate(isoDate);
    // Validado aqui, e não só na UI: este é o choke point de escrita, e a
    // camada que valida tem que ser a que grava.
    if (!local) throw new Error("Data inválida.");
    if (isFutureIsoDate(isoDate)) throw new Error("A data de conclusão não pode ser no futuro.");
    finishedAt = Timestamp.fromDate(local);
  }

  await settleWrite(updateDoc(doc(db, "user_books", bookId), {
    finishedAt,
    updatedAt: serverTimestamp(),
  }), "booksService.updateBookFinishedDate");
}

/**
 * Define ou corrige a data de INÍCIO da leitura — o par de
 * `updateBookFinishedDate`, com as mesmas regras e pelo mesmo motivo.
 *
 * Grava exclusivamente `startedAt` + `updatedAt`: sem `status`, sem
 * `progress`. Nenhum trigger lê `startedAt`, então economia não é risco hoje;
 * a disciplina é para continuar não sendo — a data em que alguém começou a
 * ler não é evento de leitura, e reescrever `status` aqui seria a armadilha
 * descrita no docblock acima.
 *
 * @param isoDate `YYYY-MM-DD` no calendário local, ou `null` para limpar.
 * @throws se a data for futura ou malformada.
 */
export async function updateBookStartedDate(
  bookId: string,
  isoDate: string | null,
): Promise<void> {
  let startedAt: Timestamp | null = null;

  if (isoDate) {
    const local = isoToLocalDate(isoDate);
    if (!local) throw new Error("Data inválida.");
    if (isFutureIsoDate(isoDate)) throw new Error("A data de início não pode ser no futuro.");
    startedAt = Timestamp.fromDate(local);
  }

  await settleWrite(updateDoc(doc(db, "user_books", bookId), {
    startedAt,
    updatedAt: serverTimestamp(),
  }), "booksService.updateBookStartedDate");
}

/**
 * Atualiza a avaliação (rating) de um livro (1-5, ou null para limpar).
 */
export async function updateBookRating(
  bookId: string,
  rating: number | null
): Promise<void> {
  await settleWrite(updateDoc(doc(db, "user_books", bookId), {
    rating,
    updatedAt: serverTimestamp(),
  }), "booksService.updateBookRating");
}

/**
 * Limite da legenda do card.
 *
 * ⚠️ Não é preferência de UI: a citação tem clamp de 2 linhas na composição
 * (Catálogo Mestre, "citação segue clamp 2"), então texto além disso não some
 * do banco — some do card, que é pior, porque o usuário só descobre depois de
 * exportar. O corte acontece aqui, no choke point de escrita, e não só no
 * `maxLength` do textarea: colar por teclado de sistema não passa pelo
 * `onChange` em todo device.
 */
export const CARD_CAPTION_MAX = 150;

/**
 * Grava a legenda do card (`cardCaption`) — ver o campo em `UserBook`.
 *
 * Choke point único de escrita: o Studio grava por aqui e o fluxo de conclusão
 * também. Frase vazia apaga o campo em vez de gravar `""` — assim
 * `hasQuote` no layout continua sendo uma pergunta só ("existe frase?"), sem
 * precisar distinguir ausente de vazio.
 */
export async function updateBookCardCaption(
  bookId: string,
  caption: string,
): Promise<void> {
  const trimmed = caption.trim().slice(0, CARD_CAPTION_MAX);
  await settleWrite(updateDoc(doc(db, "user_books", bookId), {
    cardCaption: trimmed.length > 0 ? trimmed : deleteField(),
    updatedAt: serverTimestamp(),
  }), "booksService.updateBookCardCaption");
}

/**
 * Persiste a ordem do Top 3 (`favoriteRank` 1..3) num write atômico.
 *
 * Batch e não N updates soltos: uma troca reescreve o rank dos TRÊS livros
 * (ver `buildSwapRanks`), e uma falha no meio deixaria duas capas na mesma
 * posição — exatamente o estado que a ordenação tolera mas o usuário lê como
 * "a troca não funcionou".
 */
export async function updateFavoriteRanks(
  entries: Array<{ id: string; favoriteRank: number }>,
): Promise<void> {
  if (entries.length === 0) return;
  const batch = writeBatch(db);
  for (const { id, favoriteRank } of entries) {
    batch.update(doc(db, "user_books", id), {
      favoriteRank,
      updatedAt: serverTimestamp(),
    });
  }
  await settleWrite(batch.commit(), "booksService.updateFavoriteRanks");
}
